import React from 'react'
import { css } from '@emotion/react'
import { colors, font, mq } from 'plume-ui'
import { Help, HelpProps } from '../common/Help'

/* TODO: move Table component to plume-ui */

type Props = {
  id?: string
  className?: string
  children: React.ReactNode
}

export const Table: React.FC<Props> = (props) => {
  return (
    <table
      id={props.id}
      className={props.className}
      css={css`
        ${mq.untilMedium} {
          display: block;
          white-space: nowrap;
          overflow-x: auto;
        }
        background-color: ${colors.white00};
        width: 100%;

        /* This style is necessary to use sticky header */
        border-collapse: separate;
        border-spacing: 0;

        thead {
          border-bottom: 1px solid ${colors.gray40};
          border-collapse: collapse;
        }

        th,
        td {
          border-bottom: 1px solid ${colors.gray50};
        }

        th {
          font-size: 13px;
          font-weight: ${font.weight.bold};
          white-space: nowrap;
          padding: 1rem 0.8rem 1rem 0.8rem;
          background-color: ${colors.white00};

          ${mq.untilMedium} {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
        }
      `}
      {...props}
    />
  )
}

type TableHeadProps = {
  sticky?: boolean
  offset?: string
  className?: string
  children: React.ReactNode
  dataTestId?: string
}

export const TableHead: React.FC<TableHeadProps> = (props) => {
  return (
    <thead
      className={props.className}
      css={css`
        th {
          position: ${props.sticky ? 'sticky' : 'static'};
          top: ${props.offset ?? 0};
        }
      `}
      data-test-id={props.dataTestId}
    >
      {props.children}
    </thead>
  )
}

type TableRowProps = {
  highlightOnHover?: boolean
  className?: string
  'data-appcues'?: string
  children: React.ReactNode
}

export const TableRow: React.FC<TableRowProps> = (props) => {
  return (
    <tr
      css={
        props.highlightOnHover &&
        css`
          transition: 0.3s;
          &:hover {
            background-color: ${colors.white10};
          }
        `
      }
      className={props.className}
      data-appcues={props['data-appcues']}
    >
      {props.children}
    </tr>
  )
}

type TableCellProps = {
  align?: 'left' | 'center' | 'right'
  vertical?: 'top' | 'middle'
  width?: string
  centerized?: boolean
  nowrap?: boolean
  omitted?: boolean
  empty?: boolean
  colSpan?: number
  rowSpan?: number
  className?: string
  children?: React.ReactNode
  dataTestId?: string
}

export const TableCell: React.FC<TableCellProps> = ({
  omitted = false,
  nowrap = false,
  align = 'left',
  vertical = 'top',
  centerized = false,
  ...props
}) => {
  return (
    <td
      css={css`
        font-size: 14px;
        color: ${colors.black30};
        line-height: 1.5;
        padding: 0.8rem;
        text-align: ${centerized ? 'center' : align};
        vertical-align: ${centerized ? 'middle' : vertical};
        ${props.width && `width: ${props.width};`}
        ${mq.untilMedium} {
          padding-left: 0.5rem;
          padding-right: 0.5rem;
        }
      `}
      colSpan={props.colSpan || 1}
      rowSpan={props.rowSpan || 1}
      className={props.className}
      data-test-id={props.dataTestId}
    >
      <div
        css={css`
          ${mq.untilMedium} {
            white-space: nowrap;
          }
          ${mq.large} {
            white-space: ${nowrap ? 'nowrap' : 'pre-wrap'};
          }
          ${omitted && omittedStyle}
        `}
      >
        {props.empty ? <EmptySymbol /> : props.children}
      </div>
    </td>
  )
}

const EmptySymbol = () => {
  return (
    <span
      css={css`
        text-align: center;
        margin-left: 25px;
      `}
    >
      -
    </span>
  )
}

const omittedStyle = css`
  max-height: calc(14px * 1.4 * 3);
  -webkit-line-clamp: 3;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

type TableHeadingProps = {
  align?: 'left' | 'center' | 'right'
  justify?: 'center' | 'end'
  width?: string
  sub?: string
  help?: string
  helpProps?: HelpProps
  className?: string
  children?: React.ReactNode
  dataTestId?: string
}

export const TableHeading: React.FC<TableHeadingProps> = ({
  align = 'left',
  ...props
}) => {
  return (
    <th
      css={css`
        text-align: ${align};
        ${props.width && `width: ${props.width};`}
      `}
      className={props.className}
      data-test-id={props.dataTestId}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          ${props.justify && `justify-content: ${props.justify};`}
        `}
      >
        {props.children}
        {props.sub && <SubLabel>{props.sub}</SubLabel>}
        {props.help && (
          <Help
            {...props.helpProps}
            css={css`
              margin-left: 3px;
            `}
          >
            {props.help}
          </Help>
        )}
      </div>
    </th>
  )
}

type SubLabelProps = {
  children: string
}

export const SubLabel: React.FC<SubLabelProps> = (props) => {
  return (
    <span
      css={css`
        font-size: 12px;
        font-weight: ${font.weight.normal};
        color: ${colors.textStrong};
        margin-left: 3px;
        line-height: 1.5;
      `}
    >
      {`（${props.children}）`}
    </span>
  )
}
